import { useQuery } from '@tanstack/react-query';
import { GetCoinResponse } from '@zoralabs/coins-sdk';

import { fetcher } from '@/utils/fetcher';
import { routes } from '@/utils/routes';

import { useErrorReporting } from './useErrorReporting';

type Zora20Token = GetCoinResponse['zora20Token'];

/**
 * Profile data from Zora SDK
 * @see https://docs.zora.co/coins/sdk/queries/profile#response-structure
 */
type ProfileData = {
  /** Globally unique ID for the profile */
  id?: string;
  /** Username/handle */
  handle?: string;
  /** User's display name */
  displayName?: string;
  /** User's biography/description */
  bio?: string;
  /** Username */
  username?: string;
  /** User's website URL */
  website?: string;
  /** Profile image data */
  avatar?: {
    /** Small version of profile image */
    small?: string;
    /** Medium version of profile image */
    medium?: string;
    /** Blurhash for image loading */
    blurhash?: string;
  };
  /** Public wallet information */
  publicWallet?: {
    /** User's wallet address */
    walletAddress?: string;
  };
  /** Connected social accounts */
  socialAccounts?: {
    instagram?: {
      displayName?: string;
    };
    tiktok?: {
      displayName?: string;
    };
    twitter?: {
      displayName?: string;
    };
  };
  /** Connected wallets */
  linkedWallets?: {
    edges?: Array<{
      node?: {
        walletType?: 'PRIVY' | 'EXTERNAL' | 'SMART_WALLET';
        walletAddress?: string;
      };
    }>;
  };
  /** User's creator coin (if they have one) */
  creatorCoin?: {
    /** Creator coin contract address */
    address?: string;
    /** Current market capitalization */
    marketCap?: string;
    /** 24-hour market cap change */
    marketCapDelta24h?: string;
  };
};

interface UseZoraParams {
  chainId?: number;
  address?: string;
  count?: number;
}

/**
 * Profile coins data from Zora SDK
 * @see https://docs.zora.co/coins/sdk/queries/profile-coins#response-structure
 */
type ProfileCoinsData = {
  /** Total number of created coins */
  count?: number;
  /** Array of coin edges */
  edges?: Array<{
    node?: {
      /** Coin ID */
      id?: string;
      /** Coin name */
      name?: string;
      /** Trading symbol */
      symbol?: string;
      /** Coin description */
      description?: string;
      /** Coin contract address */
      address?: string;
      /** Chain ID where coin exists */
      chainId?: number;
      /** Total supply of the coin */
      totalSupply?: string;
      /** Total trading volume */
      totalVolume?: string;
      /** 24-hour trading volume */
      volume24h?: string;
      /** Current market capitalization */
      marketCap?: string;
      /** 24h market cap change */
      marketCapDelta24h?: string;
      /** Number of unique holders */
      uniqueHolders?: number;
      /** Creation timestamp */
      createdAt?: string;
      /** Creator's wallet address */
      creatorAddress?: string;
      /** Token metadata URI */
      tokenUri?: string;
      /** Associated media */
      mediaContent?: {
        mimeType?: string;
        originalUri?: string;
        previewImage?: {
          small?: string;
          medium?: string;
          blurhash?: string;
        };
      };
      /** Uniswap V4 pool information */
      uniswapV4PoolKey?: {
        token0Address?: string;
        token1Address?: string;
        fee?: number;
        tickSpacing?: number;
        hookAddress?: string;
      };
      /** Uniswap V3 pool address */
      uniswapV3PoolAddress?: string;
    };
  }>;
  /** Pagination information */
  pageInfo?: {
    /** Whether more results exist */
    hasNextPage?: boolean;
    /** Cursor for next page */
    endCursor?: string;
  };
};

export interface ZoraToken {
  coin: Zora20Token;
  profile: ProfileData | null;
  type: 'creator' | 'post' | null;
  profileCoins: ProfileCoinsData | null;
}

export const zoraQueryKey = (params: UseZoraParams) => ['zora', params] as const;

/**
 * Fetches Zora coin data for a given chain and address.
 */
export const useZora = ({ chainId, address, count }: UseZoraParams): ZoraToken | undefined => {
  const queryParams = new URLSearchParams();
  if (chainId) queryParams.append('chainId', chainId.toString());
  if (address) queryParams.append('address', address);
  if (count !== undefined) queryParams.append('count', count.toString());

  const { data, error } = useQuery<ZoraToken, Error>({
    queryKey: zoraQueryKey({ chainId, address, count }),
    queryFn: () => fetcher(`${routes.api.ZORA}?${queryParams.toString()}`),
    enabled: Boolean(chainId && address),
    refetchOnReconnect: false,
  });

  useErrorReporting('useZora', error ?? undefined);

  return data;
};
